/**
 * <s-welcome-popup> — CMoon first-visit welcome popup
 * Opens its inner <dialog> once, shortly after the page loads, unless this
 * browser has already seen/closed it (localStorage flag). Liquid decides
 * whether the element renders at all (e.g. hidden for customers with orders).
 *
 * Attributes:
 *   storage-key — localStorage key used to remember the popup was seen
 *   delay       — milliseconds before opening (default 600)
 *   no-autoopen — never open automatically (used inside the theme editor)
 */
class SWelcomePopup extends HTMLElement {
  #dialog;
  #ac;
  #timer;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const key = this.getAttribute('storage-key') || 'cmoon-welcome-seen';

    this.#dialog.addEventListener('close', () => this.#remember(key), { signal: this.#ac.signal });

    if (this.hasAttribute('no-autoopen') || this.#seen(key)) return;

    const delay = Number(this.getAttribute('delay')) || 600;
    this.#timer = setTimeout(() => {
      if (!this.#dialog.open && !document.querySelector('dialog:modal')) this.#dialog.showModal();
    }, delay);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  #seen(key) {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }

  #remember(key) {
    try {
      localStorage.setItem(key, '1');
    } catch {
      /* storage unavailable: popup may show again, harmless */
    }
  }
}

if (!customElements.get('s-welcome-popup')) customElements.define('s-welcome-popup', SWelcomePopup);
